import React from 'react';
// import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SectionTitle from '../components/SectionTitle';
import ImageContainer from '../components/ImageContainer';

const SeniorsPage: React.FC = () => {
  // const { t } = useTranslation();

  // Benefits specific to seniors
  const benefits = [
    {
      title: "Easy to Use",
      description: "Our solutions are specially developed for people who did not grow up with the digital world.",
      icon: (
        <svg className="w-10 h-10 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
    {
      title: "No Dependence",
      description: "Your own digital presence without the control of large platforms and without hidden costs.",
      icon: (
        <svg className="w-10 h-10 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
    {
      title: "Age-Appropriate Design",
      description: "Larger font, high-contrast colors, and clear navigation for easy access.",
      icon: (
        <svg className="w-10 h-10 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
        </svg>
      ),
    },
    {
      title: "Personal Training",
      description: "We guide you step by step into the digital world with personal support.",
      icon: (
        <svg className="w-10 h-10 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
    },
  ];

  return (
    <main className="bg-light dark:bg-slate-900 transition-colors duration-300">
      {/* Hero Section - Text on right (as indicated by filename), image on left */}
      <section className="min-h-[60vh] flex items-center pt-24 pb-16 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <ImageContainer 
                src="/images/senior/forkit_senior_right.jpeg"
                alt="Digital independence for seniors"
                aspectRatio="3:2"
                className="rounded-sm shadow-lg"
              />
            </div>
            <div className="order-1 md:order-2">
              <h1 className="headline-hero">
                Digital Independence for Seniors
              </h1>
              <p className="body-large mt-6 max-w-lg">
                We help older people use the digital world without depending on complex platforms.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link 
                  to="/waitlist" 
                  className="bg-primary hover:bg-primary-600 text-white px-6 py-3 rounded-sm font-urbanist font-medium text-base shadow-sm transition-all"
                >
                  Schedule Consultation
                </Link>
                <Link 
                  to="/solutions" 
                  className="bg-transparent border border-primary text-primary dark:text-primary-300 dark:border-primary-500 px-6 py-3 rounded-sm font-urbanist font-medium text-base hover:bg-primary/5 dark:hover:bg-primary-900/30 transition-all"
                >
                  Discover Solutions
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-16 bg-white dark:bg-slate-800">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <SectionTitle 
              title="Digital Challenges in Old Age"
              subtitle="Why many seniors have difficulties with today's digital offerings"
              centered
            />
            
            <div className="mt-10 space-y-6 text-center">
              <p className="body-normal">
                Many digital platforms and services are not designed for older people. They require technical understanding, have small text and complex interfaces. Additionally, there are hidden costs, privacy concerns and constant interface changes.
              </p>
              <p className="body-normal">
                Large platforms like Facebook or Instagram are optimized for younger users. As a result: Many seniors feel excluded from the digital world or dependent on help.
              </p>
              <p className="body-normal">
                We believe it can be better. With tailored, age-appropriate solutions, we enable real digital participation - without dependence on large platforms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 bg-slate-50 dark:bg-slate-900">
        <div className="container mx-auto px-4 md:px-6">
          <SectionTitle 
            title="Benefits for Seniors"
            subtitle="Our solutions are specially tailored to the needs of older people"
            centered
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-slate-800 p-8 rounded-sm shadow-sm border border-slate-200 dark:border-slate-700"
              >
                <div className="mb-4 bg-primary-50 dark:bg-primary-900/30 p-4 inline-block rounded-full">
                  {benefit.icon}
                </div>
                <h3 className="font-urbanist font-semibold text-xl mb-3 text-slate-900 dark:text-white">
                  {benefit.title}
                </h3>
                <p className="body-normal">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 bg-white dark:bg-slate-800">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-primary-50 dark:bg-primary-950/30 p-8 rounded-sm">
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
                  <img 
                    src="/images/content/forkit_contact.jpg" 
                    alt="Margarete, 72" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-slate-700 dark:text-slate-300 text-lg italic">
                    "I had wanted a website for my craft group for a long time, but the offers were always far too complicated. With Forkit, I was finally able to create a simple, beautiful site that I can maintain myself. Now new interested parties find our group much more easily!"
                  </p>
                  <p className="mt-4 font-urbanist font-semibold text-slate-900 dark:text-white">
                    Margarete, 72
                  </p>
                  <p className="text-primary dark:text-primary-300 text-sm">
                    Leader of a craft group
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Options */}
      <section className="py-16 bg-slate-50 dark:bg-slate-900">
        <div className="container mx-auto px-4 md:px-6">
          <SectionTitle 
            title="Our Solutions for Seniors"
            subtitle="Tailor-made digital offers for people in retirement"
            centered
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="bg-white dark:bg-slate-800 rounded-sm shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="h-48">
                <img 
                  src="/images/solution/solution-website.jpg" 
                  alt="Eigene Webseite" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="font-urbanist font-semibold text-xl mb-3 text-slate-900 dark:text-white">
                  Simple Websites
                </h3>
                <p className="body-small mb-4">
                  Clear, accessible websites for clubs, groups or personal presence. With large font and clear navigation.
                </p>
                <Link 
                  to="/solutions#websites" 
                  className="font-urbanist text-sm font-medium text-primary dark:text-primary-300 hover:underline inline-flex items-center"
                >
                  Learn More <span className="ml-1">→</span>
                </Link>
              </div>
            </div>
            
            <div className="bg-white dark:bg-slate-800 rounded-sm shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="h-48">
                <img 
                  src="/images/solution/solution-booking.jpg" 
                  alt="Appointment booking" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="font-urbanist font-semibold text-xl mb-3 text-slate-900 dark:text-white">
                  Digital Group Management
                </h3>
                <p className="body-small mb-4">
                  Manage your group or club digitally. With simple member management, scheduling and messaging functions.
                </p>
                <Link 
                  to="/solutions#booking" 
                  className="font-urbanist text-sm font-medium text-primary dark:text-primary-300 hover:underline inline-flex items-center"
                >
                  Learn More <span className="ml-1">→</span>
                </Link>
              </div>
            </div>
            
            <div className="bg-white dark:bg-slate-800 rounded-sm shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="h-48">
                <img 
                  src="/images/solution/solution-newsletter.jpg" 
                  alt="Digital training" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="font-urbanist font-semibold text-xl mb-3 text-slate-900 dark:text-white">
                  Personal Training
                </h3>
                <p className="body-small mb-4">
                  Individual training and support in the use of digital offers. From setup to regular use.
                </p>
                <Link 
                  to="/solutions#training" 
                  className="font-urbanist text-sm font-medium text-primary dark:text-primary-300 hover:underline inline-flex items-center"
                >
                  Learn More <span className="ml-1">→</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-primary-50 dark:bg-primary-950/30">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="headline-section mb-6">
              Start Your Digital Independence
            </h2>
            <p className="body-large mb-8">
              We are happy to accompany you on your way into the digital world - with solutions that suit you.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link 
                to="/waitlist" 
                className="bg-primary hover:bg-primary-600 text-white px-6 py-3 rounded-sm font-urbanist font-medium text-base shadow-sm transition-all"
              >
                Arrange Free Initial Consultation
              </Link>
              <Link 
                to="/your-story"
                className="bg-transparent border border-primary text-primary dark:text-primary-300 dark:border-primary-500 px-6 py-3 rounded-sm font-urbanist font-medium text-base hover:bg-primary/5 dark:hover:bg-primary-900/30 transition-all"
              >
                Read Success Stories
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SeniorsPage; 